/**
 * Column header with an optional timeframe chip.
 *
 * Replaces AG Grid's default header so a non-daily column is distinguishable at
 * a glance — a weekly column beside its daily twin is otherwise identical, and
 * confusing the two silently misreads the data.
 *
 * Registered as `agColumnHeader`, so it applies to every column; without a
 * `timeframeBadge` param it renders exactly what the default would, minus the
 * Enterprise-only menu affordances Community doesn't have anyway.
 *
 * The Symbol column's header also carries the search's 🔍 (`symbolSearch`),
 * **always drawn**, not revealed on hover: a control that appears only under the
 * pointer is one nobody knows to look for. It turns accent while the search row
 * is open — the one cue, beside a shorter list, that rows are being hidden.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import type { IHeaderParams } from "ag-grid-community";

import { Badge } from "./ColumnPicker.js";
import type { SymbolSearch } from "../lib/symbol-search.js";

export interface TimeframeHeaderParams extends IHeaderParams {
  /** Canonical `[count]unit`, upper-cased (`W`, `2W`, `15MI`). Absent = native. */
  timeframeBadge?: string;
  /** The Symbol column only: the search this header's 🔍 opens. */
  symbolSearch?: SymbolSearch;
  /** Linked from another screen — drawn as 🔗 after the name (`screen-links.ts`). */
  linked?: boolean;
}

/** The 🔗's hover text. Which screen is the Columns list's to say; this has a header's room. */
export const LINKED_HINT = "Linked from another screen — its format is that screen's";

/**
 * The 🔍, as a drawn glass rather than the emoji: an emoji is painted in its own
 * colours and ignores `color`, so it could never turn accent — and would look
 * different on every platform the island is hosted on.
 */
function SearchToggle({ search }: { search: SymbolSearch }) {
  const open = useSyncExternalStore(search.subscribe, () => search.get().open);
  return (
    <button
      type="button"
      aria-label="Search this screen by symbol or name"
      aria-pressed={open}
      title="Search by symbol or name"
      onClick={(event) => {
        // The header's own click sorts the column; opening a search must not.
        event.stopPropagation();
        search.toggle();
      }}
      style={{ ...SEARCH_BUTTON, color: open ? "var(--qf-accent)" : "var(--qf-text-muted)" }}
    >
      <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="6.5" cy="6.5" r="4.75" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <line x1="10" y1="10" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

const SEARCH_BUTTON: React.CSSProperties = {
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  padding: 2,
  border: "none",
  background: "transparent",
  cursor: "pointer",
};

type SortState = "asc" | "desc" | null;

export function TimeframeHeader(params: TimeframeHeaderParams) {
  const { displayName, column, enableSorting, timeframeBadge, setSort, symbolSearch, linked } = params;
  const [sort, setSortState] = useState<SortState>(column.getSort() ?? null);

  // The sort indicator has to follow programmatic sorts too — applying a saved
  // sort from the workspace doesn't route through this header's click handler.
  useEffect(() => {
    const sync = () => setSortState(column.getSort() ?? null);
    column.addEventListener("sortChanged", sync);
    sync();
    return () => column.removeEventListener("sortChanged", sync);
  }, [column]);

  const cycleSort = (event: React.MouseEvent) => {
    if (!enableSorting) return;
    const next: SortState = sort === "asc" ? "desc" : sort === "desc" ? null : "asc";
    setSort(next, event.shiftKey);
  };

  return (
    <div
      onClick={cycleSort}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.3rem",
        width: "100%",
        cursor: enableSorting ? "pointer" : "default",
        overflow: "hidden",
      }}
    >
      {/* With a 🔍, the label gives up the slack so the glass sits beside the
          word it searches — `Symbol 🔍` — rather than across an empty header. */}
      <span
        style={{
          flex: symbolSearch ? "0 1 auto" : 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {displayName}
      </span>
      {linked && (
        <span
          role="img"
          aria-label={LINKED_HINT}
          title={LINKED_HINT}
          style={{ flex: "0 0 auto", fontSize: "0.7em", color: "var(--qf-text-muted)" }}
        >
          🔗
        </span>
      )}
      {symbolSearch && <SearchToggle search={symbolSearch} />}
      {symbolSearch && <span style={{ flex: 1 }} />}
      {timeframeBadge && <Badge>{timeframeBadge}</Badge>}
      {sort && <span style={{ flex: "0 0 auto", opacity: 0.7 }}>{sort === "asc" ? "▲" : "▼"}</span>}
    </div>
  );
}
